import * as React from "react";
import { LinksDialogContext } from "components/flexComponents/Links/GridView/LinkDialogContext";
import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import Links from "components/flexComponents/Links/Links";
import { UitkHeading, UitkSubheading } from "uitk-react-text";
import { UitkIcon } from "uitk-react-icons";
import { UitkCard, UitkCardContent, UitkCardTextSegment } from "uitk-react-cards";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "uitk-react-layout-flex";
import { UitkButton } from "uitk-react-button";
import { UitkLink, UitkLinkAlign } from "uitk-react-link";
import { FlexComponent } from "typings/flexFramework/FlexComponent";
import { withStores } from "stores";
import { observer } from "mobx-react";
import { DefaultFlexModuleResult } from "src/typings/microserviceModels/links-flex-module";
import { flexComponentToFlexTemplateNode } from "src/stores/FlexTemplateStore";
import { DialogProps, LinksFTNode, LinksGridItemProps } from "../typings";

export const LinksGridItem = withStores("flexModuleModelStore")(
  observer((props: LinksGridItemProps) => {
    const { view, group, routeStore, id, flexModuleModelStore } = props;
    const { modules, model } = group;
    const linksDialogContext = LinksDialogContext;
    const { title, subheading, dialogHeading, subHeadingAlignment } = model;
    const linkModules: LinksFTNode[] = modules
      .filter((module: FlexComponent) => {
        const componentModel = flexModuleModelStore.getModel(module.id) as DefaultFlexModuleResult | null;

        return componentModel?.name === "links";
      })
      .map((module: FlexComponent) => flexComponentToFlexTemplateNode(module, false));

    const openDialog = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
      event.preventDefault();
      linksDialogContext.dialogHeading = dialogHeading;
      linksDialogContext.modules = linkModules;
      routeStore.updateSearch({ pwaDialog: `linksDialog-${id}` });
    };

    const keyHelper = new ItemKeyHelper(`LinksGridItem-${id}`);

    const links = linkModules?.map((module) => (
      <div aria-hidden="true" hidden={true} key={keyHelper.next()}>
        <Links flexTemplateNode={module} key={keyHelper.next()} />
      </div>
    ));

    const dialogProps: DialogProps = { title, subheading, subHeadingAlignment, openDialog };

    let dialogView;
    if (view === "row-trigger") {
      dialogView = <DialogViewWithIcon {...dialogProps} />;
    } else if (view === "standard-link") {
      dialogView = <DialogViewLinkableText {...dialogProps} />;
    }

    return (
      <div className="LinksGridItem uitk-cell" key={keyHelper.next()}>
        <UitkCard>
          <UitkCardContent>{dialogView}</UitkCardContent>
        </UitkCard>
        <div className="is-visually-hidden">{links}</div>
      </div>
    );
  })
);

const DialogViewWithIcon: React.FC<DialogProps> = observer(({ title, subheading, openDialog }) => (
  <UitkCardTextSegment>
    <UitkButton onClick={openDialog} isFullWidth={true} aria-label={title}>
      <UitkLayoutFlex justifyContent="space-between" alignItems="center" className="LinksGridItem">
        <UitkLayoutFlexItem>
          <div className="uitk-type-left">
            <UitkHeading level={3} size={300}>
              {title}
            </UitkHeading>
            {subheading && <UitkSubheading>{subheading}</UitkSubheading>}
          </div>
        </UitkLayoutFlexItem>
        <UitkLayoutFlexItem>
          <UitkIcon name="chevron_right" />
        </UitkLayoutFlexItem>
      </UitkLayoutFlex>
    </UitkButton>
  </UitkCardTextSegment>
));

const DialogViewLinkableText: React.FC<DialogProps> = observer(
  ({ title, subheading, subHeadingAlignment, openDialog }) => (
    <UitkCardTextSegment>
      <UitkLayoutFlexItem>
        <UitkLayoutFlex justifyContent={subHeadingAlignment === "center" ? "center" : "start"}>
          <UitkLayoutFlexItem className={subHeadingAlignment === "center" ? "links-item-center" : ""}>
            <div>
              <UitkHeading level={3} size={300}>
                {title}
              </UitkHeading>
              <UitkLink
                onClick={openDialog}
                align={subHeadingAlignment === "center" ? UitkLinkAlign.CENTER : UitkLinkAlign.LEFT}
              >
                <a href="" role="button">
                  {subheading}
                </a>
              </UitkLink>
            </div>
          </UitkLayoutFlexItem>
        </UitkLayoutFlex>
      </UitkLayoutFlexItem>
    </UitkCardTextSegment>
  )
);

export default LinksGridItem;
